import styles from '../styles.module.css'

import React, { useCallback, useMemo } from 'react'

import {
  Box,
  Button,
  DiscoCallout,
  Flex,
  Heading,
  Table,
  Text,
  TextField,
} from 'components/ds'

import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import cuid from 'cuid'
import {
  RecordingSplitData,
  SplitData,
  addSplit,
  getTrackRecordingSplits,
  setSplitFieldError,
  updateSplit,
  deleteRecordingSplit,
  getTotalRecordingSplitPercentage,
  getTrackValidationErrors,
  clearSplitValidationErrors,
  getSplitsValidation,
} from '@disco/state-management/reducers/rightsManagement'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { RecordingRow } from './recording-row'
import { validateIsrc } from 'features/rights-data/utils/identifier-validation'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import Tooltip from 'components/ui/tooltip'
import { checkRecordingSplits } from './validation-errors'

export function RecordingTab({
  editedTrack,
  updateEditedTrack,
  splitsKey,
  isBulkMode,
  saveAttempted,
  allRightsHolders,
  isRightsHoldersLoading,
  backendErrorMessage,
  backendErrorTab,
  disabled = false,
  splitsDisabled = false,
}) {
  const dispatch = useDispatch()

  const splitsValidation = useSelector(state =>
    getSplitsValidation(state, splitsKey),
  )
  const validationErrors = useSelector(state =>
    getTrackValidationErrors(state, splitsKey),
  )

  const labels = useMemo(() => {
    return allRightsHolders.filter(rh => rh.entity_type === 'Label')
  }, [allRightsHolders])

  const handleIsrcChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editedTrack || !editedTrack.metadata) {
        return
      }
      const metadata = editedTrack.metadata
      updateEditedTrack({
        ...editedTrack,
        metadata: {
          ...metadata,
          ISRC: {
            ...metadata.ISRC,
            mixed: false,
            value: e.target.value,
          },
        },
      })
    },
    [editedTrack, updateEditedTrack],
  )

  const isrcNumberMeta = editedTrack.metadata?.ISRC
  const isrcNumberValue = isrcNumberMeta?.value ?? ''
  const isrcNumberIsMixed = isrcNumberMeta?.mixed ?? false

  const { valid: isIsrcValid } = isrcNumberIsMixed
    ? { valid: true }
    : validateIsrc(isrcNumberValue)

  const splits = useSelector(state => getTrackRecordingSplits(state, splitsKey))
  const totalSplitPercentage = useSelector(state =>
    getTotalRecordingSplitPercentage(state, splitsKey),
  )

  const handleUpdateSplit = useCallback(
    (splitId: number | string, changes: Partial<Omit<SplitData, 'id'>>) => {
      dispatch(updateSplit({ trackId: splitsKey, splitId, changes }))
    },
    [dispatch, splitsKey],
  )

  const handleDeleteSplit = useCallback(
    (splitId: number | string) => {
      dispatch(deleteRecordingSplit({ trackId: splitsKey, splitId }))
    },
    [dispatch, splitsKey],
  )

  const handleValidate = useCallback(
    (splitId: number | string, field: string, message: string) => {
      dispatch(
        setSplitFieldError({ trackId: splitsKey, splitId, field, message }),
      )
    },
    [dispatch, splitsKey],
  )

  const handleClearValidation = useCallback(
    (splitId: number | string) => {
      dispatch(clearSplitValidationErrors({ trackId: splitsKey, splitId }))
    },
    [dispatch, splitsKey],
  )

  const handleAddRightsHolder = useCallback(() => {
    const newSplit: RecordingSplitData = {
      id: cuid(),
      kind: 'recording',
      rightsholderId: null,
      labelId: null,
      split: 0,
      isNew: true,
    }
    dispatch(addSplit({ trackId: splitsKey, splitData: newSplit }))
  }, [dispatch, splitsKey])

  const errorMessage = checkRecordingSplits(splits, {
    isBulkMode,
    totalSplitPercentage,
    splitsValidation,
  })

  return (
    <div className={styles.container}>
      <Box mb="1">
        <Flex align="center" gap="1" mb="2">
          <Text size="2" weight="medium">
            ISRC
          </Text>
        </Flex>
        <Box className={styles.isrcField}>
          <TextField.Root
            value={isrcNumberIsMixed ? '' : isrcNumberValue}
            onChange={handleIsrcChange}
            placeholder={isrcNumberIsMixed ? 'Mixed' : 'Enter ISRC'}
            disabled={disabled}
          >
            <TextField.Slot side="right">
              {!isIsrcValid && (
                <Tooltip
                  content="This doesn’t appear to be a valid ISRC. It will be saved to the ID3 tag as entered."
                  position="top"
                >
                  <Flex>
                    <RxInfoCircled color="#ab6400" size={14} />
                  </Flex>
                </Tooltip>
              )}
            </TextField.Slot>
          </TextField.Root>
        </Box>
      </Box>
      <Box mb="4">
        <Flex justify="between" align="center" mb="1">
          <Heading size="4">Labels</Heading>
          <Button
            variant="soft"
            color="gray"
            size="2"
            onClick={handleAddRightsHolder}
            disabled={splitsDisabled}
          >
            <RxPlusCircled />
            Add
          </Button>
        </Flex>

        <Table.Root variant="surface" size="1" layout="fixed">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell width="calc(50% - 70px)" minWidth="140px">
                <Text size="2" weight="medium">
                  Rightsholder
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="calc(50% - 70px)" minWidth="140px">
                <Text size="2" weight="medium">
                  Label
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="140px" minWidth="140px">
                <Text
                  size="2"
                  weight="medium"
                  className={styles.splitColumnHeader}
                >
                  Split ({totalSplitPercentage}%)
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="5%" justify="center" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {splits.map(split => {
              const splitValidationError = validationErrors.find(
                error => error.splitId === split.id,
              )
              return (
                <RecordingRow
                  key={split.id}
                  split={split}
                  splitsKey={splitsKey}
                  onUpdate={handleUpdateSplit}
                  onDelete={handleDeleteSplit}
                  onValidate={handleValidate}
                  onClearValidation={handleClearValidation}
                  validationError={splitValidationError}
                  isRightsholdersLoading={isRightsHoldersLoading}
                  labels={labels}
                  disabled={splitsDisabled}
                />
              )
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      {errorMessage && (
        <Box mb="4">
          <DiscoCallout
            variant="surface"
            type={totalSplitPercentage > 100 ? 'error' : 'warning-important'}
          >
            {errorMessage}
          </DiscoCallout>
        </Box>
      )}

      {backendErrorMessage !== '' && (
        <Box mb="4">
          <DiscoCallout type="error">
            {backendErrorTab === 'publishing'
              ? 'We were unable to save your changes. Please resolve the error on the Publishing rights tab and try again.'
              : backendErrorMessage}
          </DiscoCallout>
        </Box>
      )}
    </div>
  )
}

export default RecordingTab
